import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { OrderItem, OrderService } from 'com.trumbull.phx.order.api-client';
import { VocabularyItemList } from 'com.trumbull.phx.vocabulary.api-client';
import { SortEvent } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { PaginatorState } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TableData, TableParams } from '../search-params.model';
import { UserDefinition } from 'com.trumbull.phx.user.api-client';
import { DocumentProcessingService } from '../../services/document-processor/document-processor.service';
import { RootObject } from '../../orders/order-details/orders.model';
import { VocabHelperService } from '../../services/vocab-helper/vocab-helper.service';
import { DateFormatterPipe } from '../../pipes/date-formatter.pipe';
import { ModalComponent } from '../../shared/modal/modal.component';
import { NeedHelpModalComponent } from '../../shared/modal/need-help-modal/need-help-modal.component';
import { SharedService } from '../../services/shared.service';
import { WorkService } from 'com.trumbull.phx.work.api-client';
import { CaseFileItemService } from 'com.trumbull.phx.casefile.api-client';
import { RecordRetrievalItemService } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import { PopoverModule } from 'primeng/popover';
import { OrderHydrationService } from '../../services/order-hydration/order-hydration.service';

@Component({
  selector: 'app-order-grid',
  templateUrl: './order-grid.component.html',
  imports: [
    CommonModule,
    TableModule,
    TooltipModule,
    PanelModule,
    RouterModule,
    ButtonModule,
    DateFormatterPipe, 
    ModalComponent,
    NeedHelpModalComponent,
    PopoverModule
  ],
  standalone: true,
})
export class OrderGridComponent {
  @ViewChild('searchResultsList') table: Table | undefined;
  @Input() orders: OrderItem[] = [];
  @Input() orderVocab: VocabularyItemList[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() error: string | null = null;
  @Input() externalUser: string = '';
  @Input() users: UserDefinition[] = [];
  @Input() tableParams:TableParams = {
    sortField: 'number',
    sortOrder: -1,
    first: 0, // For nth page (pageIndex = (n-1) → (n-1) * 10 = 10(n-1))
    rowsPerPage: 10,
    orderOwner: [],
    orderType: [],
    accountName: [],
    createdBy: [],
    clientName: []
  }
  @Output() lazyLoad = new EventEmitter<TableLazyLoadEvent>();

  

  expandedRows: { [key: string]: boolean } = {};
  recordRequests: TableData[] = [];
  accountNames: { label: string | undefined; value: string | undefined }[] = [];
  subOrdersLoading: boolean = false;
  showHelpModal: boolean = false;
  clientsVocabData: { code: string, label: string}[] = [];
  constructor(
    private readonly orderService: OrderService,
    private readonly documentProcessor: DocumentProcessingService,
    private readonly vocabHelperService: VocabHelperService,
    private readonly sharedService : SharedService,
    private readonly workService: WorkService,
    private readonly recordRetrievalObjectiveService: RecordRetrievalItemService,
    private readonly caseFileItemService: CaseFileItemService,
    private readonly orderHydrationService: OrderHydrationService
  ) {}

  ngOnInit() {
    this.fetchAccountName();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orders']) {
      this.collapseExpandedRows();
    }
  }

  onPageChange(event: PaginatorState) {
    this.storePagination(event);
    const first = event.first ?? 0;
    const rows = event.rows ?? 10;
    this.lazyLoad.emit({
      first: first,
      rows: rows,
      sortField: this.table?.sortField,
      sortOrder: this.table?.sortOrder,
    });
  }

  customSort(evt: SortEvent) {
    this.storeSortingConfig(evt)
    this.storePagination({});
    this.lazyLoad.emit({
      first: 1,
      rows: 10,
      sortField: evt.field,
      sortOrder: evt.order,
    });
  }

  collapseExpandedRows() {
    for (let key in this.expandedRows) {
      if (this.expandedRows.hasOwnProperty(key)) {
        delete this.expandedRows[key];
      }
    }
  }

  toggleRow(orderNumber: string, isCollapsed: boolean) {
    if (isCollapsed) {
      this.expandedRows = {};
    } else {
      this.expandedRows = {};
      this.expandedRows[orderNumber] = true;
      this.getSubOrderDetails(orderNumber);
    }
  }

  // Sorts record requests by formattedId using hierarchical numeric order (parents before children).
  private sortByFormattedId(rows: TableData[]): TableData[] {
    const toSegments = (v: any) => String(v ?? '').split('-').map(s => parseInt(s, 10) || 0);
    return [...rows].sort((a, b) => {
      const segsA = toSegments(a.formattedId);
      const segsB = toSegments(b.formattedId);
      const len = Math.max(segsA.length, segsB.length);
      for (let i = 0; i < len; i++) {
        const diff = (segsA[i] ?? 0) - (segsB[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });
  }

  getSubOrderDetails(orderNumber: string) {
    this.recordRequests = [];
    this.subOrdersLoading = true;
    this.orderHydrationService.hydrateOrderForGrid('standard',orderNumber).subscribe({
      next: (hydrationData) => {
        if (hydrationData) {
          this.recordRequests = this.documentProcessor.mapRequests(
            hydrationData.order,
            {
              case: hydrationData.caseData,
              retrievalObjective: hydrationData.recordRetrievalData,
              attachments: []
            } as RootObject,
            undefined,
            this.orderVocab
          );
          this.recordRequests = this.sortByFormattedId(this.recordRequests);
        }
        this.subOrdersLoading = false;
      },
      error: (err) => {
        this.subOrdersLoading = false;
        console.error('Error while combining data:', err);
      },
    });
  }

  // isExpanded(orderNumber: number): boolean {
  //   return !!this.expandedRows[orderNumber];
  // }

  getOrderTypeDisplay(code: string): string {
    if (!code) return '';
    
    const vocabItem = this.orderVocab
      .flatMap((v) => v.vocabularyItems || [])
      .find((item) => item.code === code);

    return vocabItem?.description ?? code;
  }

  getOrderStatusDisplay(code: string): string {
    if (!code) return '';
    const selectedStatus =
      this.orderVocab?.find((d: any) => d.vocabularyName === 'OrderStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === code)
      ||
      this.orderVocab?.find((d: any) => d.vocabularyName === 'RecordRetrievalRequestStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === code);
    return selectedStatus?.description ?? code;
  }

  getDisplayName(username: string): string {
      const user = this.users.find(user => user?.principalName === username);
      return user?.displayName || '';
  }

    //get all account names
  fetchAccountName(): void {
      this.vocabHelperService.getVocabData(['UserOrganization'])
      .subscribe((data) => {
        const vocabOrgTypesData = data.find(d => d.vocabularyName === 'UserOrganization');

        if (vocabOrgTypesData) {
        this.accountNames = (vocabOrgTypesData.vocabularyItems || []).map((item: any) => ({
          label: item.description,
          value: item.code,
        }));
      }
    });
  }

  getAccountName(code: string): string {
    return this.accountNames.find(account => account.value === code)?.label || code;
  }

  getClientName(code: string): string{
    this.clientsVocabData = ((this.orderVocab?.find((d: any) => d?.vocabularyName === 'RecordRetrievalCustomerCode'))?.vocabularyItems)?.map((item:any) => ({ code: item.code, label: item.description})) || [];  
    return this.clientsVocabData?.find(client => client.code === code)?.label || code;
  }

  contactUs() {
    this.showHelpModal = true;
  }

  storePagination(event: PaginatorState) {
    this.sharedService.OrderGridParams.first = event?.first ?? 0;
    this.sharedService.OrderGridParams.rowsPerPage = event?.rows ?? 10;
  }

  storeSortingConfig(evt: SortEvent) {
    this.sharedService.OrderGridParams.sortField = evt?.field ?? 'number';
    this.sharedService.OrderGridParams.sortOrder = evt?.order ?? -1;
  }
}
